import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, ArrowRight, Lightbulb, MousePointerClick, Paperclip, Send, Save, Repeat, MessageSquarePlus, PlayCircle, Download } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { icon: Download, title: "Download your best reference photo using the button above", body: "Open Step 3 (Prompt Library) and click Download on your clearest reference photo — you'll attach this file in ChatGPT.", cta: { label: "Open Step 3", href: "/app/step3", internal: true } },
  { icon: ExternalLink, title: "Open ChatGPT", body: "Head to chat.openai.com (free or Plus account both work).", cta: { label: "Open ChatGPT", href: "https://chat.openai.com" } },
  { icon: MessageSquarePlus, title: "Start a new conversation", body: "Click 'New chat' so the AI starts fresh, with no prior context." },
  { icon: Paperclip, title: "Attach one of your reference photos using the paperclip icon", body: "Pick the clearest photo from your Step 1 uploads — it teaches the AI your face." },
  { icon: MousePointerClick, title: "Paste your copied prompt into the message box", body: "Drop in the personalised prompt you copied from Step 3." },
  { icon: Send, title: "Hit send and wait for your image to generate", body: "First image usually takes 30–60 seconds. Don't refresh." },
  { icon: Save, title: "Save the generated image to your device", body: "Right-click the image and choose Save Image — or long-press on mobile." },
  { icon: Repeat, title: "Repeat with different prompts for variety!", body: "Aim for 3–5 images so you have a real set to choose from." },
];

const TIPS = [
  "Use your best, clearest reference photo for the most accurate results",
  "If the result doesn't look like you, try a different reference photo",
  "You can tweak the prompt — ask ChatGPT to adjust the lighting, outfit, or setting",
  "Generate 3–5 variations of each prompt to find your best shots",
  "Free ChatGPT accounts work, but ChatGPT Plus gives faster and sometimes better results",
];

export default function Step4Generate() {
  const navigate = useNavigate();
  const [continuing, setContinuing] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  useEffect(() => {
    api.get("/resources").then((r) => {
      const v = (r.data.resources || []).find((x) => x.title === "Step 4 Walkthrough Video");
      setVideoUrl(v?.content || "");
    }).catch(() => {});
  }, []);

  const onContinue = async () => {
    setContinuing(true);
    try {
      await api.post("/progress/step4-complete");
      toast.success("Step 4 complete — onwards to Step 5.");
      navigate("/app/step5");
    } catch {
      toast.error("Could not save progress");
    } finally {
      setContinuing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="step4-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Step 4 of 6</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Step 4: Generate in ChatGPT</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-3xl leading-relaxed">
        You've got your prompts. Now let's create some magic.
      </p>

      {/* Step cards */}
      <div className="mt-10 space-y-4">
        {STEPS.map((s, i) => (
          <div
            key={i}
            className="bsp-card bsp-card-hover p-6 md:p-7 grid md:grid-cols-12 gap-5 items-center"
            data-testid={`s4-step-${i + 1}`}
          >
            <div className="md:col-span-1 flex items-center gap-3">
              <span className="font-display text-3xl text-[#D4A853]">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className="md:col-span-1">
              <div className="w-11 h-11 rounded-xl bg-[#F5E6E0] flex items-center justify-center">
                <s.icon className="w-5 h-5 text-[#1A1F3D]" strokeWidth={1.6} />
              </div>
            </div>
            <div className="md:col-span-7">
              <h3 className="font-display text-xl md:text-2xl text-[#1A1F3D]">{s.title}</h3>
              <p className="text-[#52525b] mt-1.5 text-sm leading-relaxed">{s.body}</p>
            </div>
            <div className="md:col-span-3 flex md:justify-end">
              {s.cta && (
                s.cta.internal ? (
                  <Link
                    to={s.cta.href}
                    className="btn-primary text-sm"
                    data-testid={`s4-cta-${i + 1}`}
                  >
                    {s.cta.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <a
                    href={s.cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm"
                    data-testid="open-chatgpt"
                  >
                    {s.cta.label} <ExternalLink className="w-4 h-4" />
                  </a>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pro tips */}
      <div className="mt-10 bsp-card p-7 md:p-8 bg-[#F5E6E0]/60 border-l-4 border-[#D4A853]" data-testid="protips-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4A853] flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-[#1A1F3D]" strokeWidth={1.7} />
          </div>
          <div>
            <span className="eyebrow">Pro tips</span>
            <h2 className="font-display text-2xl text-[#1A1F3D]">Get the best results, faster</h2>
          </div>
        </div>
        <ul className="mt-5 space-y-3">
          {TIPS.map((t, i) => (
            <li key={i} className="flex items-start gap-3 text-[#2D2D2D]" data-testid={`tip-${i}`}>
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4A853] shrink-0" />
              <span className="text-sm leading-relaxed">{t}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Video */}
      <div className="mt-10 bsp-card p-6 md:p-8" data-testid="video-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#1A1F3D] flex items-center justify-center">
            <PlayCircle className="w-5 h-5 text-[#D4A853]" />
          </div>
          <div>
            <span className="eyebrow">Walkthrough video</span>
            <h2 className="font-display text-2xl text-[#1A1F3D]">See it in action</h2>
          </div>
        </div>
        {videoUrl ? (
          <div className="aspect-video rounded-lg overflow-hidden bg-black">
            <iframe
              src={videoUrl}
              title="Step 4 walkthrough"
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="aspect-video rounded-lg border-2 border-dashed border-[#e5e7eb] bg-[#FAFAF8] flex flex-col items-center justify-center text-center px-6" data-testid="video-placeholder">
            <PlayCircle className="w-12 h-12 text-[#D4A853]" strokeWidth={1.4} />
            <div className="mt-3 font-display text-xl text-[#1A1F3D]">Walkthrough video coming soon</div>
            <p className="text-sm text-[#52525b] mt-1 max-w-md">
              We're recording a fresh walkthrough — in the meantime, follow the seven steps above and you'll be good.
            </p>
          </div>
        )}
      </div>

      {/* Continue */}
      <div className="mt-10 bsp-card p-6 md:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#1A1F3D] text-white" data-testid="continue-card">
        <div>
          <div className="font-display text-2xl">Generated your first image?</div>
          <div className="text-white/70 text-sm mt-1">Mark Step 4 as complete and head to your gallery.</div>
        </div>
        <button
          onClick={onContinue}
          disabled={continuing}
          className="px-5 py-3 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-[#C29543]"
          data-testid="continue-step5"
        >
          {continuing ? "Saving…" : "Continue to Step 5"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-6 text-sm text-[#52525b]">
        Need a prompt? <Link to="/app/step3" className="gold-link">Back to Prompt Library</Link>
      </div>
    </div>
  );
}
